"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { leadCtaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as z from "@/lib/propSchema";
import { LEAD_DROP_VARIANTS } from "../../../constants";

const MetadataSchema = z.object({
  id: z.union([z.number(), z.string()]),
  meta_image: z.string().optional().default(""),
  meta_alt_image: z.string().optional().default(""),
  meta_title: z.string().optional().default(""),
  meta_sub_title: z.string().optional().default(""),
  meta_icon_bg_color: z.string().optional().default("#DADDFF"),
  meta_sub_title_color: z.string().optional().default("#505050"),
  meta_priority: z.coerce.number().optional().default(0),
});

const SectionSchema = z
  .object({
    pre_title: z.string().optional().default(""),
    pre_title_color: z.string().optional().default("#E1473F"),
    title: z.string().optional().default(""),
    title_start_color: z.string().optional().default("#171719"),
    subtitle: z.string().optional().default(""),
    // Do not coerce undefined → ""; optional unset must stay undefined for conditional render
    section_background_image: z
      .union([z.string(), z.null()])
      .optional()
      .transform((v) => {
        if (v === undefined) return undefined;
        if (v === null) return "";
        const s = String(v).trim();
        return s === "" ? "" : s;
      }),
    section_overlay_rgba: z.string().optional().default("rgba(236,236,236,0.69)"),
    button_text: z.string().optional().default(""),
    button_color: z.string().optional().default("#DB5803"),
    button_text_color: z.string().optional().default("#FFFFFF"),
    button_icon_url: z.string().optional().default(""),
    button_icon_alt: z.string().optional().default(""),
    metadata: z.array(MetadataSchema).optional().default([]),
    status: z.union([z.boolean(), z.number()]).optional(),
  })
  .transform((d) => ({
    ...d,
    metadata: [...(d.metadata || [])].sort(
      (a, b) => (a.meta_priority || 0) - (b.meta_priority || 0),
    ),
  }));

type CardRow = z.infer<typeof MetadataSchema>;

/**
 * Render a heading where `{...}`-braced text uses `accentColor` and the rest
 * uses `baseColor` (same convention as Banner5/BannerFeatureGrid).
 * The two heading fields cross their colors: in `Heading` the braced part gets
 * the remainder color, and in `Heading — remainder` the braced part gets the
 * highlight color.
 */
function renderAccentBraces(text: string, baseColor: string, accentColor: string) {
  return text.split(/(\{[^}]*\})/g).map((part, i) => {
    if (!part) return null;
    const braced = part.length > 1 && part[0] === "{" && part.endsWith("}");
    return (
      <span key={i} style={{ color: braced ? accentColor : baseColor }}>
        {braced ? part.slice(1, -1) : part}
      </span>
    );
  });
}

function BenefitCard({ row }: { row: CardRow }) {
  const rawBg = row.meta_icon_bg_color?.trim();
  const noIconTile =
    !rawBg ||
    rawBg === "none" ||
    rawBg === "transparent" ||
    rawBg === "#00000000";
  const iconBg = rawBg || "#DADDFF";
  const bodyColor = row.meta_sub_title_color || "#505050";

  return (
    <article
      className={cn(
        "flex flex-col rounded-[10px] w-full shadow-[0px_4px_21.3px_0px_rgba(0,0,0,0.21)] bg-[#FFFF]",
        // Mobile (Figma 2162:12391): 304×280, centered stack, 16px padding, 12px gap icon→text
        "max-w-[304px] mx-auto min-h-[280px] h-[280px] items-center justify-center p-4 gap-3",
        // Tablet / desktop: original grid card layout (keep width capped so cards don't balloon in the 2-col band)
        "min-[640px]:max-w-[304px] min-[640px]:mx-auto min-[640px]:min-h-[301px] min-[640px]:h-full min-[640px]:items-start min-[640px]:justify-start min-[640px]:pl-[13px] min-[640px]:pr-[29px] min-[640px]:py-6 min-[640px]:gap-[23px]",
      )}
    >
      {noIconTile ? (
        <div className="relative flex shrink-0 w-14 h-14 items-center justify-center min-[640px]:w-auto min-[640px]:h-auto min-[640px]:items-start">
          {row.meta_image ? (
            <Image
              src={row.meta_image}
              alt={row.meta_alt_image || row.meta_title || "Benefit"}
              width={56}
              height={56}
              className="object-contain size-14 shrink-0"
            />
          ) : (
            <span
              className="block size-14 rounded bg-black/5 shrink-0"
              aria-hidden
            />
          )}
        </div>
      ) : (
        <div
          className="flex flex-col items-start p-[14.021px] rounded-[8.413px] shrink-0 min-[640px]:items-start"
          style={{ backgroundColor: iconBg }}
        >
          <div className="relative size-14 shrink-0 flex items-center justify-center">
            {row.meta_image ? (
              <Image
                src={row.meta_image}
                alt={row.meta_alt_image || row.meta_title || "Benefit"}
                width={56}
                height={56}
                className="object-contain size-14 shrink-0"
              />
            ) : (
              <span className="block size-14 rounded bg-white/40 shrink-0" aria-hidden />
            )}
          </div>
        </div>
      )}

      <div
        className={cn(
          "flex flex-col w-full min-w-0",
          "gap-[6px] items-center text-center tracking-[-0.24px]",
          "min-[640px]:gap-[14px] min-[640px]:items-start min-[640px]:text-left min-[640px]:tracking-normal",
        )}
      >
        <h3
          className={cn(
            "font-semibold text-[#171719] w-full",
            "text-[20px] leading-[26px] max-w-[267px] mx-auto",
            "min-[640px]:text-[18px] min-[640px]:leading-normal min-[640px]:max-w-[240px] min-[640px]:mx-0",
          )}
        >
          {row.meta_title}
        </h3>
        <p
          className={cn(
            "text-[16px] leading-normal font-normal w-full min-h-[72px] max-w-[266px] mx-auto",
            "min-[640px]:min-h-0 min-[640px]:max-w-[218px] min-[640px]:mx-0",
          )}
          style={{
            color: bodyColor,
          }}
        >
          {row.meta_sub_title}
        </p>
      </div>
    </article>
  );
}

function ArrowUpRight() {
  return (
    <svg
      aria-hidden
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      className="size-5 shrink-0"
    >
      <path
        d="M7 17L17 7M9 7h8v8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Mobile-only tap-to-flip card, matching ProgramBenefitsGrid3's compact
 * 168x212 cards. Unlike Grid3, the back face keeps the card's own white
 * background (no accent-color swap) — only the "View More/less" affordance
 * uses `accent` (the heading remainder color, title_start_color). Desktop
 * keeps <BenefitCard/> unchanged; this component never renders at >=640px.
 */
function MobileFlipCard({ row, accent }: { row: CardRow; accent: string }) {
  const [flipped, setFlipped] = useState(false);
  const desc = row.meta_sub_title?.trim() || "";
  const bodyColor = row.meta_sub_title_color?.trim() || "#505050";

  const rawBg = row.meta_icon_bg_color?.trim();
  const noIconTile =
    !rawBg ||
    rawBg === "none" ||
    rawBg === "transparent" ||
    rawBg === "#00000000";
  const iconBg = rawBg || "#DADDFF";

  const iconEl = row.meta_image ? (
    <Image
      src={row.meta_image}
      alt={row.meta_alt_image || row.meta_title || "Benefit"}
      width={40}
      height={40}
      className="object-contain size-10 shrink-0"
    />
  ) : (
    <span className="block size-10 rounded bg-black/5 shrink-0" aria-hidden />
  );

  const iconBlock = noIconTile ? (
    <div className="relative flex size-10 shrink-0 items-center justify-center">
      {iconEl}
    </div>
  ) : (
    <div
      className="flex shrink-0 rounded-[8px] p-2"
      style={{ backgroundColor: iconBg }}
    >
      {iconEl}
    </div>
  );

  // No description -> nothing to flip to, render a plain static card.
  if (!desc) {
    return (
      <article className="relative flex h-[212px] w-full flex-col gap-3 overflow-hidden rounded-[10px] bg-[#FFFFFF] p-4 shadow-[0px_4px_21.3px_0px_rgba(0,0,0,0.21)]">
        {iconBlock}
        <p className="font-semibold text-[16px] leading-[20px] text-[#171719]">
          {row.meta_title}
        </p>
      </article>
    );
  }

  const toggle = () => setFlipped((f) => !f);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
      aria-label={row.meta_title}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      }}
      className={cn(
        "relative h-[212px] w-full cursor-pointer select-none rounded-[10px] [perspective:1000px]",
        "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black/40",
      )}
    >
      {/*
        Faces stacked in one grid cell (grid-area 1/1). transform-style /
        backface-visibility are set inline with -webkit- prefixes because the
        Tailwind v4 + Lightning CSS pipeline emits none and iOS Safari < 15.4
        needs them, else the back face bleeds over the front.
      */}
      <div
        className={cn(
          "grid h-full w-full [&>*]:[grid-area:1/1]",
          "motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-out",
          flipped && "[transform:rotateY(180deg)]",
        )}
        style={{
          transformStyle: "preserve-3d",
          WebkitTransformStyle: "preserve-3d",
        }}
      >
        {/* Front (white) */}
        <article
          className="flex h-full w-full flex-col gap-3 overflow-hidden rounded-[10px] bg-[#FFFFFF] p-4 shadow-[0px_4px_21.3px_0px_rgba(0,0,0,0.21)]"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          {iconBlock}
          <p className="font-semibold text-[16px] leading-[20px] text-[#171719]">
            {row.meta_title}
          </p>
          <div
            className="mt-auto flex items-center gap-1 font-semibold text-[14px]"
            style={{ color: accent }}
          >
            <span>View More</span>
            <ArrowUpRight />
          </div>
        </article>

        {/* Back (same white background — no color swap) */}
        <article
          className="flex h-full w-full flex-col gap-2 overflow-hidden rounded-[10px] bg-[#FFFFFF] p-4 shadow-[0px_4px_21.3px_0px_rgba(0,0,0,0.21)]"
          style={{
            transform: "rotateY(180deg)",
            WebkitTransform: "rotateY(180deg)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          <p
            className="text-[14px] leading-[20px] font-normal"
            style={{ color: bodyColor }}
          >
            {desc}
          </p>
          <div
            className="mt-auto flex items-center gap-1 font-semibold text-[14px]"
            style={{ color: accent }}
          >
            <span>View less</span>
            <ArrowUpRight />
          </div>
        </article>
      </div>
    </div>
  );
}

export default function ProgramBenefitsGrid2({ data }: { data: any }) {
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const raw = data?.data ?? data ?? {};
  const parsed = useMemo(() => SectionSchema.safeParse(raw), [raw]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_benefits_grid_2");
  //   }
  // }, []);

  if (!parsed.success) {
    return null;
  }

  const section = parsed.data;
  const isOff =
    section.status === false ||
    section.status === 0 ||
    String(section.status) === "0";

  if (isOff) {
    return null;
  }

  const rows = section.metadata?.filter((m) => m.meta_title?.trim()) ?? [];
  const hasHeader =
    Boolean(section.pre_title?.trim()) ||
    Boolean(section.title?.trim()) ||
    Boolean(section.subtitle?.trim());

  if (rows.length === 0 && !hasHeader) {
    return null;
  }

  const highlight = section.pre_title_color || "#E1473F";
  const restColor = section.title_start_color || "#171719";
  const bgUrl =
    typeof section.section_background_image === "string" &&
    section.section_background_image.trim() !== ""
      ? section.section_background_image.trim()
      : "";

  const ctaText = section.button_text?.trim() || "";
  const ctaBg = section.button_color?.trim() || "#DB5803";
  const ctaColor = section.button_text_color?.trim() || "#FFFFFF";
  const ctaIcon = section.button_icon_url?.trim() || "";
  const ctaIconAlt = section.button_icon_alt?.trim() || ctaText;

  const handleCtaClick = () => {
    leadCtaClick({
      section: "program_benefits_grid_2",
      component: "cta_button",
      label: ctaText,
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  };

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative isolate w-full flex flex-col items-center justify-center overflow-hidden",
        // Mobile (Figma 2162:12391): 34px vertical rhythm, 16px horizontal inset
        "gap-[34px] py-8 px-4",
        "min-[640px]:gap-[50px] min-[640px]:py-12 min-[640px]:px-8 min-[768px]:py-[72px] min-[768px]:px-[99px]",
      )}
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      {bgUrl ? (
        <>
          <div
            className="absolute inset-0 z-0 pointer-events-none overflow-hidden"
            aria-hidden
          >
            {/* next/image fill breaks inside admin PreviewRenderer (transform: scale); native img is reliable */}
            <img
              src={bgUrl}
              alt=""
              className="absolute inset-0 z-0 h-full w-full min-h-full min-w-full object-cover object-center scale-[1.15] min-[640px]:scale-110 min-[768px]:scale-105 -translate-x-[5%]"
              decoding="async"
            />
          </div>
        </>
      ) : (
        <div
          className="absolute inset-0 pointer-events-none bg-[#FFFFFF]"
          aria-hidden
        />
      )}

      {(section.pre_title || section.title || section.subtitle) && (
        <div
          className={cn(
            "relative z-[1] flex flex-col items-center w-full",
            "gap-[7px]",
            "min-[640px]:gap-4",
          )}
        >
          <div
            className={cn(
              "w-full max-w-[314px] min-[640px]:max-w-3xl flex flex-col items-center text-center",
              "gap-[7px] min-[640px]:gap-4",
            )}
          >
            {(section.pre_title || section.title) && (
              <h2
                className={cn(
                  "w-full text-center",
                  // Highlight + remainder are one continuous inline run. The
                  // 630px cap (vs the 3xl block around it) mirrors the reference
                  // Launchpad's md:w-[630px] and is deliberately just under a
                  // full-width headline at 32px Poppins SemiBold, so long titles
                  // wrap at the last space instead of running edge to edge. The
                  // exact value matters: "The MCA That Makes You Unstoppable"
                  // measures 625px, so it only stays whole (with "in Tech" on
                  // line 2) above ~625 — at 600 it would break after "You".
                  // No balancing: the tail drops alone, as on the live page.
                  "min-[640px]:max-w-[630px] min-[640px]:mx-auto",
                  "font-semibold text-[#171719]",
                )}
              >
                {section.pre_title?.trim() ? (
                  <span
                    className={cn(
                      "font-semibold",
                      "text-[28px] leading-[1.26] tracking-[-0.82px]",
                      "min-[640px]:text-[32px] min-[640px]:tracking-normal min-[640px]:leading-tight",
                    )}
                    style={{ color: highlight }}
                  >
                    {renderAccentBraces(section.pre_title.trim(), highlight, restColor)}
                  </span>
                ) : null}
                {section.pre_title?.trim() && section.title?.trim() ? " " : null}
                {section.title?.trim() ? (
                  <span
                    className={cn(
                      "font-semibold",
                      "text-[28px] leading-[1.26] tracking-[-0.82px]",
                      "min-[640px]:text-[32px] min-[640px]:tracking-normal min-[640px]:leading-tight",
                    )}
                    style={{ color: restColor }}
                  >
                    {renderAccentBraces(section.title.trim(), restColor, highlight)}
                  </span>
                ) : null}
              </h2>
            )}
            {section.subtitle ? (
              <p
                className={cn(
                  "w-full font-normal text-[#171719]",
                  "text-[16px] leading-[22px]",
                  "min-[640px]:text-[18px] min-[640px]:leading-normal min-[640px]:text-[#373737]",
                )}
              >
                {section.subtitle}
              </p>
            ) : null}
            {ctaText && (
              <div className="mt-2 min-[640px]:mt-3 flex w-full justify-center">
                <button
                  type="button"
                  onClick={handleCtaClick}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 cursor-pointer",
                    "rounded-[10px] font-semibold transition-[transform,box-shadow] duration-200 ease-out",
                    "px-6 py-3 text-[15px] min-[640px]:px-8 min-[640px]:py-4 min-[640px]:text-[16px]",
                    "hover:-translate-y-[1px] hover:shadow-[0px_8px_20px_rgba(0,0,0,0.15)]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  )}
                  style={{ backgroundColor: ctaBg, color: ctaColor }}
                >
                  <span>{ctaText}</span>
                  {ctaIcon ? (
                    <Image
                      src={ctaIcon}
                      alt={ctaIconAlt}
                      width={18}
                      height={18}
                      className="size-[18px] shrink-0 object-contain"
                    />
                  ) : (
                    <svg
                      aria-hidden
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="size-[18px] shrink-0"
                    >
                      <path
                        d="M5 12h14M13 5l7 7-7 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div
          className={cn(
            "relative z-[1] w-full max-w-[914px] flex flex-col gap-[34px]",
          )}
        >
          {/*
            flex-wrap + justify-center (not grid): a 3-col grid left-aligns an
            orphan last row (e.g. 5 cards -> 2 cards stuck in cols 1-2). The
            per-breakpoint widths below reproduce the old grid cell sizes
            (2 cols at sm, 3 cols at lg) so full rows look identical, while a
            short last row centers.
          */}
          <div
            className={cn(
              // 2 cards per row on mobile too; a lone last card centers via
              // flex-wrap + justify-center.
              "flex flex-row flex-wrap justify-center items-stretch gap-4 w-full",
              "min-[640px]:gap-[34px]",
            )}
          >
            {rows.map((row) => (
              <div
                key={String(row.id)}
                className={cn(
                  "flex basis-[calc((100%-1rem)/2)] max-w-[168px]",
                  "min-[640px]:basis-auto min-[640px]:w-[calc((100%-34px)/2)] min-[640px]:max-w-none",
                  "min-[1024px]:w-[calc((100%-68px)/3)]",
                )}
              >
                {/* Tablet / desktop (>=640px): unchanged card */}
                <div className="hidden w-full min-[640px]:flex">
                  <BenefitCard row={row} />
                </div>
                {/* Mobile (<640px): tap to flip and reveal the description */}
                <div className="flex w-full min-[640px]:hidden">
                  <MobileFlipCard row={row} accent={restColor} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
